'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  ArrowDown01Icon,
  ArrowUp01Icon,
  HelpCircleIcon,
  Add01Icon,
  Cancel01Icon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { Button } from '../../components/button';
import { Collapsible } from '../../components/collapsible';
import { LegacyDialog } from '../../components/dialog';
import { IconButton } from '../../components/icon-button';
import {
  BodyDefault,
  BodyStrong,
  MetaDefault,
} from '../../components/typography';
import {
  AddPathDraftColumnHeaders,
  AddPathDraftRow,
  type PathDraftRowState,
} from './AddPathDraftRow';
import {
  buildPathsFromDraftRows,
  canSubmitPathDraftRows,
  createEditPathDraftRow,
  createEmptyPathDraftRow,
  getPathDraftRowPathError,
} from './add-paths-dialog.utils';
import {
  applyPathDraftScopeToggle,
  type PathScopeDraft,
  type PathScopeKind,
} from './domain-scope.utils';

type AddPathsDialogProps = {
  open: boolean;
  domain: string;
  existingPaths: string[];
  editPath?: string;
  editKind?: PathScopeKind;
  editIsInFunctionalityScope?: boolean;
  editIsInTestingScope?: boolean;
  editIsThirdParty?: boolean;
  isPrimaryHost?: boolean;
  onOpenChange: (open: boolean) => void;
  onAddPaths: (paths: PathScopeDraft[]) => void;
  onEditPath?: ({
    previousPath,
    path,
    kind,
    isInFunctionalityScope,
    isInTestingScope,
    isThirdParty,
  }: {
    previousPath: string;
    path: string;
    kind: PathScopeKind;
    isInFunctionalityScope: boolean;
    isInTestingScope: boolean;
    isThirdParty: boolean;
  }) => void;
};

export const AddPathsDialog = ({
  open,
  domain,
  existingPaths,
  editPath,
  editKind,
  editIsInFunctionalityScope = true,
  editIsInTestingScope = true,
  editIsThirdParty = false,
  isPrimaryHost = false,
  onOpenChange,
  onAddPaths,
  onEditPath,
}: AddPathsDialogProps) => {
  const t = useTranslations('designSystem.domainScope');
  const tCommon = useTranslations('designSystem.common');
  const [rows, setRows] = useState<PathDraftRowState[]>([
    createEmptyPathDraftRow(),
  ]);
  const [isHelpExpanded, setIsHelpExpanded] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const isEditMode = editPath !== undefined;
  const validationMessages = useMemo(
    () => ({
      incompleteRowError: t('addPathsDialog.incompleteRowError'),
      pathContainsSpacesError: t('pathContainsSpacesError'),
      pathInvalidCharactersError: t('pathInvalidCharactersError'),
      pathInvalidStructureError: t('pathInvalidStructureError'),
      duplicatePathError: t('duplicatePathError'),
    }),
    [t],
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    if (isEditMode && editPath !== undefined) {
      setRows([
        createEditPathDraftRow({
          path: editPath,
          kind: editKind ?? 'allowlist',
          isInFunctionalityScope: editIsInFunctionalityScope,
          isInTestingScope: editIsInTestingScope,
          isThirdParty: isPrimaryHost ? false : editIsThirdParty,
        }),
      ]);
    } else {
      setRows([createEmptyPathDraftRow()]);
    }

    setIsHelpExpanded(false);
    setHasAttemptedSubmit(false);
  }, [
    editIsInFunctionalityScope,
    editIsInTestingScope,
    editIsThirdParty,
    editKind,
    editPath,
    isEditMode,
    isPrimaryHost,
    open,
  ]);
  const reservedPaths = useMemo(
    () =>
      isEditMode && editPath !== undefined
        ? existingPaths.filter((existingPath) => existingPath !== editPath)
        : existingPaths,
    [editPath, existingPaths, isEditMode],
  );
  const updateRow = useCallback(
    (rowId: string, updater: (row: PathDraftRowState) => PathDraftRowState) => {
      setRows((previousRows) =>
        previousRows.map((row) => (row.id === rowId ? updater(row) : row)),
      );
    },
    [],
  );
  const handleAddRow = () => {
    setRows((previousRows) => [...previousRows, createEmptyPathDraftRow()]);
  };

  const handleRemoveRow = (rowId: string) => {
    setRows((previousRows) => {
      if (previousRows.length <= 1) {
        return previousRows;
      }

      return previousRows.filter((row) => row.id !== rowId);
    });
  };

  const canSubmit = useMemo(
    () =>
      canSubmitPathDraftRows({
        draftRows: rows,
        reservedPaths,
      }),
    [rows, reservedPaths],
  );
  const pathErrorsByRowId = useMemo(() => {
    const errors = new Map<string, string | null>();

    rows.forEach((row) => {
      errors.set(
        row.id,
        getPathDraftRowPathError({
          row,
          draftRows: rows,
          reservedPaths,
          messages: validationMessages,
          showEmptyErrors: hasAttemptedSubmit,
        }),
      );
    });

    return errors;
  }, [hasAttemptedSubmit, rows, reservedPaths, validationMessages]);
  const handleSave = () => {
    setHasAttemptedSubmit(true);
    if (!canSubmitPathDraftRows({ draftRows: rows, reservedPaths })) {
      return;
    }

    const paths = buildPathsFromDraftRows(rows, { isPrimaryHost });

    if (isEditMode && editPath !== undefined) {
      const [editedPath] = paths;

      if (!editedPath) {
        return;
      }

      onEditPath?.({
        previousPath: editPath,
        path: editedPath.path,
        kind: editedPath.kind,
        isInFunctionalityScope: editedPath.isInFunctionalityScope ?? false,
        isInTestingScope: editedPath.isInTestingScope ?? false,
        isThirdParty: editedPath.isThirdParty ?? false,
      });
    } else {
      onAddPaths(paths);
    }

    onOpenChange(false);
  };

  return (
    <LegacyDialog.Root
      open={open}
      onOpenChange={(details) => onOpenChange(details.open)}
    >
      <LegacyDialog.Backdrop />
      <LegacyDialog.Positioner>
        <LegacyDialog.Content width="512px" maxWidth="90%">
          <VStack gap={2} alignItems="stretch" px={6} pt={6} pb={3}>
            <HStack
              justifyContent="space-between"
              alignItems="center"
              gap="2.5"
            >
              <LegacyDialog.Title fontWeight="medium" flexGrow={1}>
                {isEditMode
                  ? t('editPathsDialog.title')
                  : t('addPathsDialog.title')}
              </LegacyDialog.Title>
              <LegacyDialog.CloseTrigger asChild>
                <IconButton
                  aria-label={tCommon('close')}
                  variant="ghost"
                  size="xs"
                >
                  <Cancel01Icon />
                </IconButton>
              </LegacyDialog.CloseTrigger>
            </HStack>
            <LegacyDialog.Description>
              {isEditMode
                ? t('editPathsDialog.description')
                : t.rich('addPathsDialog.description', {
                    domain,
                    bold: (chunks) => (
                      <BodyStrong as="span">{chunks}</BodyStrong>
                    ),
                  })}
            </LegacyDialog.Description>

            {!isEditMode ? (
              <Collapsible.Root
                open={isHelpExpanded}
                onOpenChange={({ open: expanded }) =>
                  setIsHelpExpanded(expanded)
                }
                w="full"
              >
                <Box
                  w="full"
                  borderWidth="1px"
                  borderColor="border.default"
                  borderRadius="md"
                  overflow="hidden"
                >
                  <Collapsible.Trigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      w="full"
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      borderRadius="none"
                      px={3}
                      py={2.5}
                      fontWeight="normal"
                    >
                      <HStack gap={2} minW={0} flex={1}>
                        <HelpCircleIcon size={16} />
                        <MetaDefault textAlign="left">
                          {t('addPathsDialog.pathRulesDescriptionTitle')}
                        </MetaDefault>
                      </HStack>
                      {isHelpExpanded ? (
                        <Box flexShrink={0}>
                          <ArrowUp01Icon size={16} />
                        </Box>
                      ) : (
                        <Box flexShrink={0}>
                          <ArrowDown01Icon size={16} />
                        </Box>
                      )}
                    </Button>
                  </Collapsible.Trigger>
                  <Collapsible.Content>
                    <VStack gap={3} alignItems="stretch" px={4} pb={4} pt={1}>
                      <VStack gap={1} alignItems="stretch">
                        <BodyDefault>
                          {t('addPathsDialog.subpathsBullet')}
                        </BodyDefault>
                        <MetaDefault color="fg.muted" pl={4}>
                          {t('addPathsDialog.subpathsExample')}
                        </MetaDefault>
                      </VStack>
                      <VStack gap={1} alignItems="stretch">
                        <BodyDefault>
                          {t('addPathsDialog.wildcardBullet')}
                        </BodyDefault>
                        <MetaDefault color="fg.muted" pl={4}>
                          {t('addPathsDialog.wildcardExample')}
                        </MetaDefault>
                      </VStack>
                      <VStack gap={1} alignItems="stretch">
                        <BodyDefault>
                          {t('addPathsDialog.caseSensitiveBullet')}
                        </BodyDefault>
                        <MetaDefault color="fg.muted" pl={4}>
                          {t('addPathsDialog.caseSensitiveExample')}
                        </MetaDefault>
                      </VStack>
                    </VStack>
                  </Collapsible.Content>
                </Box>
              </Collapsible.Root>
            ) : null}
          </VStack>

          <VStack gap={3} px={6} py={3} alignItems="stretch">
            <VStack gap={2} alignItems="stretch">
              <AddPathDraftColumnHeaders />
              {rows.map((row) => (
                <AddPathDraftRow
                  key={row.id}
                  row={row}
                  domain={domain}
                  pathError={pathErrorsByRowId.get(row.id)}
                  canRemove={!isEditMode && rows.length > 1}
                  isPrimaryHost={isPrimaryHost}
                  onPathChange={(event) => {
                    updateRow(row.id, (currentRow) => ({
                      ...currentRow,
                      path: event.target.value,
                    }));
                  }}
                  onKindChange={(kind) => {
                    updateRow(row.id, (currentRow) => {
                      if (kind === 'blocklist') {
                        return {
                          ...currentRow,
                          kind,
                          isInFunctionalityScope: false,
                          isInTestingScope: false,
                          isThirdParty: false,
                        };
                      }

                      return {
                        ...currentRow,
                        kind,
                        ...(currentRow.kind !== 'allowlist'
                          ? {
                              isInFunctionalityScope: true,
                              isInTestingScope: true,
                              isThirdParty: false,
                            }
                          : {}),
                      };
                    });
                  }}
                  onFunctionalityChange={(enabled) => {
                    updateRow(row.id, (currentRow) =>
                      applyPathDraftScopeToggle({
                        draft: currentRow,
                        field: 'functionality',
                        enabled,
                      }),
                    );
                  }}
                  onTestingChange={(enabled) => {
                    updateRow(row.id, (currentRow) =>
                      applyPathDraftScopeToggle({
                        draft: currentRow,
                        field: 'testing',
                        enabled,
                      }),
                    );
                  }}
                  onThirdPartyChange={(enabled) => {
                    updateRow(row.id, (currentRow) =>
                      applyPathDraftScopeToggle({
                        draft: currentRow,
                        field: 'thirdParty',
                        enabled,
                      }),
                    );
                  }}
                  onRemove={() => handleRemoveRow(row.id)}
                />
              ))}
            </VStack>

            {!isEditMode ? (
              <Box alignSelf="flex-start">
                <Button
                  variant="outline"
                  size="xs"
                  disabled={!canSubmit}
                  onClick={handleAddRow}
                >
                  <HStack gap={1} alignItems="center">
                    <Add01Icon size={14} />
                    <MetaDefault>{t('addPathsDialog.addPathRow')}</MetaDefault>
                  </HStack>
                </Button>
              </Box>
            ) : null}
          </VStack>

          <HStack gap={2} justifyContent="flex-end" pt={3} pb={6} px={6}>
            <LegacyDialog.CloseTrigger asChild>
              <Button variant="outline" size="sm">
                {tCommon('cancel')}
              </Button>
            </LegacyDialog.CloseTrigger>
            <Button size="sm" disabled={!canSubmit} onClick={handleSave}>
              {isEditMode ? tCommon('save') : tCommon('add')}
            </Button>
          </HStack>
        </LegacyDialog.Content>
      </LegacyDialog.Positioner>
    </LegacyDialog.Root>
  );
};
